export interface ILoan {
    category: TLoanCategory;
    // REVIEW
    isSecondary: boolean;
    propertyID: string;
    id: string;
    name: string | null;
    amount: number | null;
    purchaseDetailsID: string | null;
    /**
     * Down payment information.
     *
     * Either `downPayment` (absolute amount in dollars) or
     * `downPaymentPercentage` (percentage of property price) should be set.
     *
     * ⚠️ Only one should be provided at a time.
     */
    downPayment: number | null;
    downPaymentPercentage: number | null;
    term: number;
    program: TProgramType;
    customProgram: string;
    type: TLoanType;
    rate: number;
    points: number;
    /**
     * Mortgage insurance information.
     *
     * Either `MI` (absolute amount in dollars) or
     * `annualMIPercentage` (percentage of loan amount) should be set.
     *
     * ⚠️ Only one should be provided at a time.
     */
    MI: number | null;
    annualMIPercentage: number | null;
    /**
     * Upfront MI information.
     *
     * Either `upfrontMI` (absolute amount in dollars) or
     * `upfrontMIPercentage` (percentage of loan amount) should be set.
     *
     * ⚠️ Only one should be provided at a time.
     */
    upfrontMI: number | null;
    upfrontMIPercentage: number | null;
    buydownCosts: number | null;
    closingCosts: number | null;
    /**
     * @deprecated Use {@link ILoan.userPrepaidEscrows} instead
     */
    prepaidEscrows?: number;
    userPrepaidEscrows: number | null;
    escrows: { insurance: number | null; tax: number | null; interest: number | null };
    ccID: string | null;
    closingCostObj: ICCObj | null;
    ARM: {
        frequency: 6 | 12;
        scenario: "worst" | "histAVG" | "current" | "custom";
        initialCap: number;
        adjustmentCap: number;
        lifetimeCap: number;
        customRates: number[];
        sofrToday?: number;
    };
    secondLoan: ISecondLoan;
    customAPR: number | null;
    customAPRCost: number | null;
    MIWorksheet: IMIWorksheet;
    userBalance: number | null;
    didYouRefi: boolean;
    refiCost: number;
    /**
     * @deprecated Use {@link ILoan.startDate} instead
     */
    refiDate?: { month: number; year: number };
    // month: 1 - 12, year: 2023
    startDate: { month: number; year: number };
    userMonthlyPayment: number | null;
    frontRatioMax: number;
    backRatioMax: number;
    targetLTV: number;
    maxDownPayment: number | null;
    selfInsured: boolean;
    userCashToApply: number | null;
    hasCashApplied: boolean;
    /** @deprecated Use additionalMonthlyPayments instead */
    additionalMonthlyPayment: number;
    additionalMonthlyPayments: IMonthlyAdditionalPayment[];
    /** Easy way to toggle off additional payments. */
    useAdditionalMonthlyPayment: boolean;
    userAdditionalMonthlyPayment: number | null;
    isFinancedCC: boolean;
    isFinancedPrepaidEscrow: boolean;
    isFinancedPoints: boolean;
    lumpPayments: LumpSumPayment[];
    cowWaiting: 2 | 1.5 | 1 | 0.5;
    cowLoans: ICowLoan[];
    hidden: boolean;
    /** does not include buydown credits which are applied to enhancedLoan.credits */
    // NOTE will affect existing clients
    additionalCredits: number | null;
    creditsObj: ICCCategory | null;
    estimatedHomeValue: number | null;
    applyAvailableCashBeforeDebtsToDownPayment: boolean;
    refiType: REFI_TYPES | null;
    LTVLimit: number;
    /** @deprecated on 06-01-2024 */
    credits?: number | null;
    investment: IInvestment;
}

export interface IInvestment {
    startingBalance: number;
    rate: number;
    periodPayment: IMonthlyAdditionalPayment[];
}

export enum LOAN_CATEGORY {
    PURCHASE = "purchase",
    EXISTING = "existing",
    REFINANCE = "refinance",
    COW = "costOfWaiting",
}
export type TLoanCategory = `${LOAN_CATEGORY}`;

export enum PROGRAM_TYPES {
    CONVENTIONAL = "conventional",
    FHA = "fha",
    USDA = "usda",
    VA = "va",
    INTEREST5 = "interest5",
    INTEREST7 = "interest7",
    INTEREST10 = "interest10",
    ALL_CASH = "allCash",
    OTHER = "other",
}
export type TProgramType = `${PROGRAM_TYPES}`;

export enum LOAN_TYPES {
    FIXED = "fixed",
    ARM_HALF = "armHalf",
    ARM_1 = "arm1",
    ARM_3 = "arm3",
    ARM_5 = "arm5",
    ARM_7 = "arm7",
    ARM_9 = "arm9",
    ARM_10 = "arm10",
    ARM_11 = "arm11",
    ARM_15 = "arm15",
    BD_10 = "bd10",
    BD_11 = "bd11",
    BD_21 = "bd21",
    BD_321 = "bd321",
}
export type TLoanType = `${LOAN_TYPES}`;

export interface ICCSubCategory {
    APR: boolean;
    rate: number;
    usePercent: boolean;
    percentOf: "property" | "loan";
    isCredit: boolean;
}
export interface ICCCategory {
    [key: string]: ICCSubCategory;
}
export interface ICCWorksheet {
    [key: string]: ICCCategory;
}
export interface ICCObj {
    id: string;
    created: string;
    name: string;
    data: ICCWorksheet;
}

export type TSecondLoanType = "fixed" | "float5" | "float10";
export interface ISecondLoan {
    type: TSecondLoanType;
    ltv: number;
    term: number;
    amount: number | null;
    rate: number;
}

export interface IMIWorksheet {
    isMonthly: boolean;
    isRefund: boolean;
    isFinanced: boolean;
    activeModifiers: string[];
}

export interface LumpSumPayment {
    /** total lump amount */
    amount: number;
    /** which payment number payment is added */
    payment: number;
    /** if future payment amounts should be adjusted */
    recast: boolean;
}

export interface IMonthlyAdditionalPayment {
    /** if firstPaymentNumber is undefined, we apply from the first payment */
    firstPaymentNumber?: number;
    /** If lastPaymentNumber is undefined, we apply through the end of the mortgage */
    lastPaymentNumber?: number;
    amount: number;
}

export interface ICowLoan extends ILoan {}

export enum REFI_TYPES {
    /** Loan amount is hard set by the target LTV and all costs/financing gets taking away from the cash instead of added to the loan amount */
    STRICT_LTV = "strictLTV",
    LOOSE_LTV = "looseLTV",
}
